import React, { useState, useContext, useEffect } from 'react';
import moment from 'moment';
import { SystemSettingsContext } from '../../../contexts/SystemSettingsContext';
import {
    IconDelete,
    IconClone,
    IconEdit,
    useModal,
    Cell,
    Header,
    GutterCap,
    Gutter
} from '@adserve/adserve-react-components';
import {
    panelContentContainerStyle,
    panelTitleContainerStyle,
    panelTitleStyle,
    listContainerStyle
} from './OffAirCostsStyles';
import { toCurrency, GetOffAirCostForBackend } from './OffAirCostsUtils';
import { CreateOffair, DeleteOffair, GetOffairType, UpdateOffair } from '../../../services/CampaignsDataService';
import { AirtimePlanningContext } from '../../../contexts/AirtimePlanningContext';
import { BusinessTypes, OffairStatuses } from '../../../constants/Enums';

const whiteButtonStyle = {
    border: '2px solid #CCCCCC',
    backgroundColor: '#FFFFFF',
    cursor: 'pointer'
};

const headRowHeight = 40;
const visibleRows = 12;
const rowHeight = 40;
const maxVisibleHeight = rowHeight * (visibleRows + 3); // + 3 for Header rows and Total row

const Row = ({
    oc = {},
    i,
    currentOffairCost,
    onActionClick,
    readOnly,
    formatCurrency,
    scrollPx,
    scrollableLength,
    setScrollPx,
    campaign,
    selectedProperties
}) => {
    const onEditClick = () => onActionClick('edit', oc);

    const onDeleteClick = () => onActionClick('delete', oc);

    const GetPropertyName = (id) => {
        const result = selectedProperties.find((x) => x.id === id);
        return result ? result.name : '';
    };

    const offAirCostStatusName = (status) => {
        let retVal;
        switch (status) {
            case OffairStatuses.DRAFT:
                retVal = 'Draft';
                break;
            case OffairStatuses.QUOTED:
                retVal = 'Quoted';
                break;
            case OffairStatuses.BOOKED:
                retVal = 'Booked';
                break;
            case OffairStatuses.BILLED:
                retVal = 'Billed';
                break;
            case OffairStatuses.CANCELLED:
                retVal = 'Cancelled';
                break;
            default:
                retVal = status;
                break;
        }
        return retVal;
    };

    const cellStyle = { color: readOnly || oc.status > OffairStatuses.BOOKED ? '#cccccc' : 'black' };

    let commission = 0;
    let vat = 0;
    if (oc.commission > 0) {
        commission = (oc.commission / 100) * oc.price;
    }
    if (oc.vat > 0) {
        vat = (oc.vat / 100) * (oc.price - commission);
    }

    return (
        <>
            <Cell
                index={i}
                scrollPx={scrollPx}
                style={{ borderWidth: '0 1px 1px 1px' }}
                visibleRows={visibleRows}
                rowHeight={rowHeight}
            >
                <IconEdit
                    style={{
                        width: 21,
                        height: 21,
                        fill: oc.id && oc.id === currentOffairCost?.id ? '#FAA800' : ''
                    }}
                    onClick={onEditClick}
                    disabled={readOnly || oc.status > OffairStatuses.QUOTED}
                />
            </Cell>

            <Cell index={i} scrollPx={scrollPx} visibleRows={visibleRows} rowHeight={rowHeight}>
                <IconDelete
                    style={{ width: 21, height: 21 }}
                    onClick={onDeleteClick}
                    disabled={readOnly || oc.status > OffairStatuses.BOOKED}
                />
            </Cell>

            <Cell index={i} scrollPx={scrollPx} visibleRows={visibleRows} rowHeight={rowHeight}>
                {oc.offairTypeName}
            </Cell>

            <Cell index={i} scrollPx={scrollPx} visibleRows={visibleRows} rowHeight={rowHeight}>
                {oc.description}
            </Cell>

            <Cell index={i} scrollPx={scrollPx} visibleRows={visibleRows} rowHeight={rowHeight}>
                {oc.stationId === window.emptyGuid ? 'Not billed to any station' : oc.stationName}
            </Cell>

            <Cell index={i} scrollPx={scrollPx} visibleRows={visibleRows} rowHeight={rowHeight}>
                {oc.price > 0 ? formatCurrency(oc.price) : oc.price ? toCurrency(oc.price) : ''}
            </Cell>

            <Cell index={i} scrollPx={scrollPx} visibleRows={visibleRows} rowHeight={rowHeight}>
                {commission !== 0 && toCurrency(commission)}
            </Cell>

            <Cell index={i} scrollPx={scrollPx} visibleRows={visibleRows} rowHeight={rowHeight}>
                {vat !== 0 && toCurrency(vat)}
            </Cell>

            <Cell index={i} scrollPx={scrollPx} visibleRows={visibleRows} rowHeight={rowHeight}>
                {oc.startDate ? moment(oc.startDate).format('MMM DD YYYY') : ''}
            </Cell>

            <Cell index={i} scrollPx={scrollPx} visibleRows={visibleRows} rowHeight={rowHeight}>
                {oc.endDate ? moment(oc.endDate).format('MMM DD YYYY') : ''}
            </Cell>

            <Cell index={i} scrollPx={scrollPx} visibleRows={visibleRows} rowHeight={rowHeight}>
                {oc.offairTypeRevenueAttribution === true
                    ? oc.billingDate && moment(oc.billingDate).valueOf() > 0
                        ? moment(oc.billingDate).format('MMM DD YYYY')
                        : ''
                    : oc.allocatedType === 1
                    ? window.translate('Monthly (Pro Rata)')
                    : oc.allocatedType !== undefined && window.translate('Daily (Pro Rata)')}
            </Cell>

            {campaign?.businessType?.toLowerCase() === BusinessTypes.SANDP.name.toLowerCase() && (
                <Cell index={i} scrollPx={scrollPx} visibleRows={visibleRows} rowHeight={rowHeight}>
                    {oc?.propertyName}
                </Cell>
            )}

            <Cell index={i} scrollPx={scrollPx} style={cellStyle} visibleRows={visibleRows} rowHeight={rowHeight}>
                {offAirCostStatusName(oc.status)}
            </Cell>

            {/* Add gutter on first data row, that spans the visible rows */}
            {i === 0 && (
                <Gutter
                    maxVisibleLength={visibleRows * rowHeight}
                    scrollableLength={scrollableLength}
                    scrollPx={scrollPx}
                    onScroll={setScrollPx}
                    visibleRows={visibleRows}
                />
            )}

            {/* Fill in the grid cells below the gutter. This is never visible */}
            {i >= visibleRows && <div />}
        </>
    );
};

const TotalRow = ({ scrollPx, rowHeight, visibleRows, offairActivities, campaign }) => {
    const { formatCurrency } = useContext(SystemSettingsContext);

    const sumAllLines = (field) => {
        let total = 0;
        offairActivities.forEach((b) => {
            if (b.status !== 5) {
                // Cancelled
                total = total + parseFloat(b[field]);
            }
        });
        return total;
    };

    let totalCommission = 0;
    let totalVat = 0;

    for (const oa in offairActivities) {
        if (offairActivities[oa].status === 5) {
            // Cancelled
            continue;
        }
        const commission =
            offairActivities[oa].commission > 0
                ? (offairActivities[oa].commission / 100) * offairActivities[oa].price
                : 0;
        const vat =
            offairActivities[oa].vat > 0
                ? (offairActivities[oa].vat / 100) * (offairActivities[oa].price - commission)
                : 0;
        totalCommission += commission;
        totalVat += vat;
    }

    return (
        <>
            <Cell
                scrollPx={scrollPx}
                style={{ borderWidth: '0 1px 1px 1px', opacity: 1 }}
                visibleRows={visibleRows}
                rowHeight={rowHeight}
            ></Cell>

            <Cell scrollPx={scrollPx} visibleRows={visibleRows} rowHeight={rowHeight} style={{ opacity: 1 }}></Cell>

            <Cell scrollPx={scrollPx} visibleRows={visibleRows} rowHeight={rowHeight} style={{ opacity: 1 }}>
                {window.translate('Total Offairs')}
            </Cell>

            <Cell scrollPx={scrollPx} visibleRows={visibleRows} rowHeight={rowHeight} style={{ opacity: 1 }}></Cell>

            <Cell scrollPx={scrollPx} visibleRows={visibleRows} rowHeight={rowHeight} style={{ opacity: 1 }}></Cell>

            <Cell scrollPx={scrollPx} visibleRows={visibleRows} rowHeight={rowHeight} style={{ opacity: 1 }}>
                {sumAllLines('price') > 0 ? formatCurrency(sumAllLines('price')) : ''}
            </Cell>

            <Cell scrollPx={scrollPx} visibleRows={visibleRows} rowHeight={rowHeight} style={{ opacity: 1 }}>
                {totalCommission > 0 ? formatCurrency(totalCommission) : ''}
            </Cell>

            <Cell scrollPx={scrollPx} visibleRows={visibleRows} rowHeight={rowHeight} style={{ opacity: 1 }}>
                {totalVat > 0 ? formatCurrency(totalVat) : ''}
            </Cell>

            <Cell scrollPx={scrollPx} visibleRows={visibleRows} rowHeight={rowHeight} style={{ opacity: 1 }}></Cell>

            <Cell scrollPx={scrollPx} visibleRows={visibleRows} rowHeight={rowHeight} style={{ opacity: 1 }}></Cell>

            {campaign?.businessType?.toLowerCase() === BusinessTypes.SANDP.name.toLowerCase() && (
                <Cell scrollPx={scrollPx} visibleRows={visibleRows} rowHeight={rowHeight} style={{ opacity: 1 }}></Cell>
            )}

            <Cell scrollPx={scrollPx} visibleRows={visibleRows} rowHeight={rowHeight} style={{ opacity: 1 }}></Cell>

            <Cell
                scrollPx={scrollPx}
                visibleRows={visibleRows}
                rowHeight={rowHeight}
                style={{ opacity: 1, width: 139 }}
            ></Cell>
        </>
    );
};

const CurrentOffAirCosts = ({
    readOnly = false,
    offairActivities,
    currentOffairCost,
    setCurrentOffairCost,
    loadOffairs,
    handleClear,
    setOffairActivities,
    offairsLoader,
    campaign
}) => {
    // Context
    const { formatCurrency } = useContext(SystemSettingsContext);
    const { setOffairChanges, selectedProperties, propertyStats } = useContext(AirtimePlanningContext);

    // State
    const { addMessage } = useModal();
    const [scrollPx, setScrollPx] = useState(0);

    const scrollableLength = rowHeight * offairActivities.length;

    useEffect(() => {}, [offairsLoader]);

    const handleActionClick = (command, oc) => {
        switch (command) {
            case 'edit':
                editOffAirCost(oc);
                break;
            case 'copy':
                addMessage({
                    message: window.translate(`Are you sure you want to copy ${oc.offairTypeName}?`),
                    buttons: [
                        {
                            text: window.translate('No'),
                            style: { ...whiteButtonStyle, width: 87 },
                            callback: null
                        },
                        {
                            text: window.translate('Yes'),
                            style: { cursor: 'pointer' },
                            callback: () => copyOffAirCost(oc)
                        }
                    ]
                });
                break;
            case 'delete':
                addMessage({
                    message: window.translate(
                        `Are you sure you want to ${oc.status === OffairStatuses.DRAFT ? 'delete' : 'cancel'} ${
                            oc.offairTypeName
                        }?`
                    ),
                    buttons: [
                        {
                            text: window.translate('No'),
                            style: { ...whiteButtonStyle, width: 87 },
                            callback: null
                        },
                        {
                            text: window.translate('Yes'),
                            style: { cursor: 'pointer' },
                            callback: () => deleteOffAirCost(oc)
                        }
                    ]
                });
                break;
            default:
                break;
        }
    };

    const editOffAirCost = (oc) => {
        setCurrentOffairCost(oc);
    };

    const copyOffAirCost = async (oc) => {
        const data = await GetOffAirCostForBackend({
            ...oc,
            id: window.newGuid()
        });
        await CreateOffair(data);
        handleClear();
        loadOffairs();
    };

    const deleteOffAirCost = async (oc) => {
        if (oc.status === OffairStatuses.DRAFT) {
            await DeleteOffair(oc.id);
        } else {
            await UpdateOffair({ ...oc, status: OffairStatuses.CANCELLED });
        }
        campaign.modified = true;
        handleClear();
        setScrollPx(0);
        loadOffairs();
        setOffairChanges(true);
    };

    const onQuoteChange = (oc, value) => {
        oc.quote = value;
        if (offairActivities?.length > 0) {
            const allOffAirs = offairActivities.map((a) => {
                return { ...a };
            });
            setOffairActivities(allOffAirs);
        }
    };

    const fillEmptyRows = () => {
        const emptyRows = [];
        const ocLength = offairActivities ? offairActivities?.length : 0;
        for (let i = ocLength; i < visibleRows; i++) {
            emptyRows.push(<Row key={i} i={i} readOnly scrollPx={scrollPx} campaign={campaign} />);
        }
        return emptyRows;
    };

    // ASSP-8116 Fix mangled table for Bespoke billing - gridTemplateColumns assigned as 
    //
    // gridTemplateColumns: `40px 40px repeat(${
    //     campaign?.billingTermName !== 'Bespoke'
    //         ? 9
    //         : campaign?.businessType?.toLowerCase() === BusinessTypes.SANDP.name.toLowerCase()
    //         ? 10
    //         : 8
    // },minmax(max-content,auto)) 120px 19px`,
    //
    // The 8 should 9 and S&P is Bespoke so using the original calcColumns()
    //
    const calcColumns = () => {
        return campaign?.businessType?.toLowerCase() === BusinessTypes.SANDP.name.toLowerCase() ? 10 : 9;
    };
    const gridTempCols = `40px 40px repeat(${calcColumns()},minmax(max-content,auto)) 120px 19px`;

    return (
        <div style={panelContentContainerStyle}>
            <div style={panelTitleContainerStyle}>
                <div style={panelTitleStyle}>{window.translate('Current Off-Air Costs')}</div>
            </div>
            <div style={listContainerStyle}>
                <div
                    style={{
                        width: '100%',
                        display: 'grid',
                        gridTemplateColumns: gridTempCols,
                        gridAutoRows: 40,
                        height: maxVisibleHeight,
                        overflowY: 'hidden',
                        overflowX: 'scroll'
                    }}
                >
                    <Header
                        style={{
                            borderWidth: 1,
                            gridColumn: '1 / 3'
                        }}
                    >
                        {window.translate('Actions')}
                    </Header>

                    <Header rowHeight={headRowHeight} scrollPx={scrollPx}>
                        {window.translate('Billing Category')}
                    </Header>

                    <Header rowHeight={headRowHeight} scrollPx={scrollPx}>
                        {window.translate('Description')}
                    </Header>

                    <Header rowHeight={headRowHeight} scrollPx={scrollPx}>
                        {window.translate('Station')}
                    </Header>

                    <Header rowHeight={headRowHeight} scrollPx={scrollPx}>
                        {window.translate('Price')}
                    </Header>

                    <Header rowHeight={headRowHeight} scrollPx={scrollPx}>
                        {window.translate('Commission')}
                    </Header>

                    <Header rowHeight={headRowHeight} scrollPx={scrollPx}>
                        {window.translate('VAT')}
                    </Header>

                    <Header rowHeight={headRowHeight} scrollPx={scrollPx}>
                        {window.translate('Start Date')}
                    </Header>

                    <Header rowHeight={headRowHeight} scrollPx={scrollPx}>
                        {window.translate('End Date')}
                    </Header>

                    <Header rowHeight={headRowHeight} scrollPx={scrollPx}>
                        {window.translate('Invoice Date')}
                    </Header>

                    {campaign?.businessType?.toLowerCase() === BusinessTypes.SANDP.name.toLowerCase() && (
                        <Header rowHeight={headRowHeight} scrollPx={scrollPx}>
                            {window.translate('Property')}
                        </Header>
                    )}

                    <GutterCap rowHeight={headRowHeight} scrollPx={scrollPx}>
                        {window.translate('Status')}
                    </GutterCap>

                    {offairActivities.map((oc, i) => (
                        <Row
                            key={i}
                            i={i}
                            oc={oc}
                            currentOffairCost={currentOffairCost}
                            readOnly={readOnly}
                            onActionClick={handleActionClick}
                            formatCurrency={formatCurrency}
                            onQuoteChange={onQuoteChange}
                            scrollPx={scrollPx}
                            scrollableLength={scrollableLength}
                            setScrollPx={setScrollPx}
                            campaign={campaign}
                            selectedProperties={propertyStats}
                        />
                    ))}

                    {fillEmptyRows()}
                    <TotalRow
                        scrollPx={
                            offairActivities.length >= visibleRows
                                ? (offairActivities.length - visibleRows) * rowHeight
                                : 0
                        }
                        rowHeight={rowHeight}
                        visibleRows={visibleRows}
                        offairActivities={offairActivities}
                        campaign={campaign}
                    />
                </div>
            </div>
        </div>
    );
};

export default CurrentOffAirCosts;
